import React, { useEffect, useMemo, useState } from "react";
import ToolShell from "@/components/ToolShell";
import { track } from "@/lib/analytics";
import { Award, Clock, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScholarshipsPage() {
  const [all, setAll] = useState([]);
  const [tags, setTags] = useState([]);
  const [active, setActive] = useState([]);
  const [minAmount, setMinAmount] = useState(0);
  const [effort, setEffort] = useState("");

  useEffect(() => {
    track.tool_opened("scholarships");
    fetch(`${API}/scholarships`).then(r => r.json()).then(d => {
      setAll(d.scholarships);
      setTags(d.allTags);
    });
  }, []);

  const filtered = useMemo(() => {
    return all.filter(s => {
      if (active.length && !s.tags.some(t => active.includes(t))) return false;
      if (minAmount && (s.amount || 0) < minAmount) return false;
      if (effort && s.effort !== effort) return false;
      return true;
    });
  }, [all, active, minAmount, effort]);

  const toggleTag = (t) => {
    setActive(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
    track.scholarship_filtered();
  };

  return (
    <ToolShell wide tool="Tool 06 · Scholarship Finder" title="60+ scholarships, filtered your way." subtitle="Curated from high-trust sources. Low-effort ones are highlighted, start there. AidWaive doesn't track which scholarships you click.">
      <div className="card mb-6" data-testid="scholarships-filters">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <label className="text-sm text-ink-muted">Effort:</label>
          {["", "low", "medium", "high"].map(e => (
            <button key={e || "any"} onClick={() => setEffort(e)} className={`px-3 py-1 rounded-full text-xs border ${effort === e ? "border-accent text-accent bg-accent/10" : "border-border text-ink-muted hover:text-ink"}`} data-testid={`effort-${e || "any"}`}>
              {e === "" ? "Any" : e}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <label className="text-sm text-ink-muted">Min $</label>
            <input type="number" min="0" className="input !h-9 !py-0 w-28 text-sm" value={minAmount} onChange={e => setMinAmount(Number(e.target.value) || 0)} data-testid="min-amount" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {tags.map(t => (
            <button key={t} onClick={() => toggleTag(t)} className={`px-3 py-1 rounded-full text-xs border transition-colors ${active.includes(t) ? "border-accent text-accent bg-accent/10" : "border-border text-ink-muted hover:text-ink"}`} data-testid={`tag-${t}`}>
              {t.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </div>

      <div className="text-sm text-ink-dim mb-4" data-testid="scholarships-count">{filtered.length} scholarship{filtered.length === 1 ? "" : "s"} matching your filters</div>

      <div className="grid sm:grid-cols-2 gap-4" data-testid="scholarships-grid">
        {filtered.map(s => (
          <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" onClick={() => track.scholarship_clicked()} className="card block hover:border-border-hover group" data-testid={`scholarship-${s.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <Award className="w-5 h-5 text-accent mt-1" />
              {s.effort === "low" && <span className="privacy-pill" style={{ color: "#f59e0b", background: "rgba(245,158,11,0.08)", borderColor: "rgba(245,158,11,0.25)" }}>LOW EFFORT</span>}
            </div>
            <h3 className="font-heading text-xl text-ink mb-1 group-hover:text-accent transition-colors">{s.name}</h3>
            <div className="flex items-center gap-3 text-xs text-ink-dim mb-3">
              {s.amount > 0 && <span>Up to ${s.amount.toLocaleString()}</span>}
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {s.deadline}</span>
            </div>
            <p className="text-sm text-ink-muted leading-relaxed">{s.summary}</p>
            <div className="mt-4 text-xs text-accent flex items-center gap-1">Apply on their site <ExternalLink className="w-3 h-3" /></div>
          </a>
        ))}
      </div>
    </ToolShell>
  );
}
